import { EditOutlined } from "@mui/icons-material";
import { Button } from "@mui/material";
import { isArray, isNil } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import DateInput from "../../../components/inputs/DateInput";
import DollarInput from "../../../components/inputs/DollarInput";
import PercentInput from "../../../components/inputs/PercentInput";
import SelectInput from "../../../components/inputs/SelectInput";
import InputWrapper from "../../../components/inputs/shared/InputWrapper";
import TextInput from "../../../components/inputs/TextInput";
import Flx from "../../../components/layout/Flx";
import BasicModal from "../../../components/modals/BasicModal";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanPipelineClear } from "../../../redux/actions/loanPipelineActions";

const CellRendererEditableLoanField = (params) => {
  const [show, setShow] = useState(null);
  // console.log("params", params?.data?.loanName, params);
  const { data, colDef } = params;
  const loanData = useMemo(() => params?.data || {}, [params]);
  const field = useMemo(() => params?.colDef?.field, [params]);

  const fieldLabel = useMemo(() => params?.colDef?.headerName, [params]);
  const fieldType = useMemo(() => {
    if (params?.colDef?.type) {
      return params?.colDef?.type;
    }

    if (isArray(params?.colDef?.cellEditorParams?.values)) {
      return "select";
    }
    return "text";
  }, [params]);

  const fieldSelectOptions = useMemo(
    () => params?.colDef?.cellEditorParams?.values || [],
    [params],
  );
  return (
    <>
      <Button
        sx={{ fontSize: "inherit", fontWeight: 400, color: "inherit" }}
        variant="text"
        onClick={() => setShow(params)}
      >
        {params?.valueFormatted || params?.value}
        <EditOutlined
          sx={{ fontSize: "16px", ml: 0.5 }}
          color="primary"
          className="thin-9"
        />
      </Button>
      <UpdateLoanDataModal
        show={show}
        loanId={data?._id}
        loanData={loanData}
        field={field}
        fieldLabel={fieldLabel}
        fieldType={fieldType}
        fieldSelectOptions={fieldSelectOptions}
        onClose={() => setShow(null)}
      />
    </>
  );
};

const UpdateLoanDataModal = ({
  show,
  loanData,
  field,
  fieldLabel,
  fieldType,
  fieldSelectOptions,
  onClose,
}) => {
  const { loading, updateLoanData, updateMilestoneStatus } =
    useUnderwritingHook();
  const dispatch = useDispatch();
  const [newValue, setNewValue] = useState(null);

  const currentValue = useMemo(() => loanData?.[field], [loanData, field]);

  const loanName = useMemo(
    () => loanData?.loanName || loanData?.loanNumber,
    [loanData],
  );

  const handleOnClose = useCallback(() => {
    setNewValue(null);
    onClose();
  }, [onClose]);

  const onUpdateClick = () => {
    const loanId = loanData?._id;

    if (field === "loanStatus") {
      updateMilestoneStatus({
        loanId: loanId,
        loanStatus: newValue,
        onSuccessFn: (res) => {
          dispatch(loanPipelineClear());
          handleOnClose();
        },
      });
      return;
    }

    updateLoanData({
      loanId: loanId,
      data: { [field]: newValue },
      onSuccessFn: (res) => {
        dispatch(loanPipelineClear());
        handleOnClose();
      },
    });
  };

  useEffect(() => {
    setNewValue(loanData?.[field] || null);
  }, [loanData, field]);

  if (isNil(show)) {
    return;
  }

  return (
    <BasicModal
      show={show}
      title={"Update Loan"}
      // title={modalTitle}
      description={`<${loanData?.loanNumber}> ${loanName}`}
      onClose={handleOnClose}
      maxWidth={"xs"}
    >
      <Flx column gap={3}>
        {fieldType === "text" ? (
          <InputWrapper label={fieldLabel}>
            <TextInput value={newValue} onChange={setNewValue} />
          </InputWrapper>
        ) : null}
        {fieldType === "dollar" ? (
          <InputWrapper label={fieldLabel}>
            <DollarInput value={newValue} onChange={setNewValue} />
          </InputWrapper>
        ) : null}
        {fieldType === "percent" ? (
          <InputWrapper label={fieldLabel}>
            <PercentInput value={newValue} onChange={setNewValue} />
          </InputWrapper>
        ) : null}
        {fieldType === "date" ? (
          <InputWrapper label={fieldLabel}>
            <DateInput value={newValue} onChange={setNewValue} />
          </InputWrapper>
        ) : null}
        {fieldType === "select" ? (
          <InputWrapper label={fieldLabel}>
            <SelectInput
              value={newValue}
              onChange={setNewValue}
              options={fieldSelectOptions}
            />
          </InputWrapper>
        ) : null}

        <Flx fw end>
          <Button
            size="large"
            loading={loading}
            disabled={currentValue === newValue}
            onClick={onUpdateClick}
          >
            Update Loan
          </Button>
        </Flx>
      </Flx>
    </BasicModal>
  );
};

export default CellRendererEditableLoanField;
